const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');
const normalizeCountryCode = (value) => String(value ?? '')
    .trim()
    .toLowerCase();
const stripDialCode = (digits, dialCode) => {
    if (!digits)
        return '';
    if (dialCode && digits.startsWith(dialCode)) {
        return digits.slice(dialCode.length);
    }
    return digits;
};
const normalizeNationalDigits = (countryCode, nationalDigits) => {
    if (!nationalDigits)
        return '';
    if (!nationalDigits.startsWith('0'))
        return nationalDigits;
    if (countryCode === 'nl' && /^0\d{9}$/.test(nationalDigits)) {
        return nationalDigits.replace(/^0/, '');
    }
    if (countryCode === 'be' && /^0\d{9}$/.test(nationalDigits)) {
        return nationalDigits.replace(/^0/, '');
    }
    if (countryCode === 'se' && /^0\d{9}$/.test(nationalDigits)) {
        return nationalDigits.replace(/^0/, '');
    }
    if (countryCode === 'de' && /^0\d{10,11}$/.test(nationalDigits)) {
        return nationalDigits.replace(/^0/, '');
    }
    if (countryCode === 'fr' && /^0\d{9}$/.test(nationalDigits)) {
        return nationalDigits.replace(/^0/, '');
    }
    return nationalDigits;
};
const inferDialCode = (countryCode, dialCode) => {
    if (dialCode)
        return dialCode;
    if (countryCode === 'nl')
        return '31';
    if (countryCode === 'be')
        return '32';
    if (countryCode === 'fr')
        return '33';
    if (countryCode === 'se')
        return '46';
    if (countryCode === 'de')
        return '49';
    return '';
};
const inferCountryCode = (countryCode, dialCode, digits) => {
    if (dialCode === '31' || digits.startsWith('31'))
        return 'nl';
    if (dialCode === '32' || digits.startsWith('32'))
        return 'be';
    if (dialCode === '33' || digits.startsWith('33'))
        return 'fr';
    if (dialCode === '46' || digits.startsWith('46'))
        return 'se';
    if (dialCode === '49' || digits.startsWith('49'))
        return 'de';
    if (/^0?6\d{8}$/.test(digits))
        return 'nl';
    if (countryCode)
        return countryCode;
    return '';
};
const formatNationalByCountry = (countryCode, nationalDigits) => {
    if (countryCode === 'nl' && nationalDigits.length > 1) {
        return `${nationalDigits.slice(0, 1)} ${nationalDigits.slice(1)}`;
    }
    if (countryCode === 'be' && /^\d{9}$/.test(nationalDigits)) {
        return `${nationalDigits.slice(0, 3)} ${nationalDigits.slice(3, 5)} ${nationalDigits.slice(5, 7)} ${nationalDigits.slice(7)}`;
    }
    if (countryCode === 'se' && /^\d{9}$/.test(nationalDigits)) {
        return `${nationalDigits.slice(0, 2)} ${nationalDigits.slice(2, 5)} ${nationalDigits.slice(5, 7)} ${nationalDigits.slice(7)}`;
    }
    if (countryCode === 'de' && /^\d{10,11}$/.test(nationalDigits)) {
        return `${nationalDigits.slice(0, 3)} ${nationalDigits.slice(3)}`;
    }
    if (countryCode === 'fr' && /^\d{9}$/.test(nationalDigits)) {
        return `${nationalDigits.slice(0, 1)} ${nationalDigits.slice(1, 3)} ${nationalDigits.slice(3, 5)} ${nationalDigits.slice(5, 7)} ${nationalDigits.slice(7)}`;
    }
    return nationalDigits;
};
export const formatPhoneByCountry = (rawValue, dialCode, countryCode) => {
    const raw = String(rawValue ?? '').trim();
    if (!raw)
        return '';
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    const normalizedDialCode = digitsOnly(dialCode);
    const normalizedRaw = raw.startsWith('00') ? `+${raw.slice(2)}` : raw;
    const digits = digitsOnly(normalizedRaw);
    const effectiveCountryCode = inferCountryCode(normalizedCountryCode, normalizedDialCode, digits);
    const inferredDialCode = inferDialCode(effectiveCountryCode, normalizedDialCode);
    const nationalDigits = normalizeNationalDigits(effectiveCountryCode, stripDialCode(digits, inferredDialCode));
    const dialPrefix = inferredDialCode || normalizedDialCode;
    if (!dialPrefix) {
        if (normalizedRaw.startsWith('+'))
            return normalizedRaw;
        return digits ? `+${digits}` : raw;
    }
    if (!nationalDigits)
        return `+${dialPrefix}`;
    const formattedNational = formatNationalByCountry(effectiveCountryCode, nationalDigits);
    return `+${dialPrefix} ${formattedNational}`.trim();
};
const isValidNationalDigits = (countryCode, nationalDigits) => {
    if (!nationalDigits)
        return false;
    if (countryCode === 'nl') {
        return /^\d{9}$/.test(nationalDigits);
    }
    if (countryCode === 'be') {
        return /^\d{9}$/.test(nationalDigits);
    }
    if (countryCode === 'se') {
        return /^\d{9}$/.test(nationalDigits);
    }
    if (countryCode === 'de') {
        return /^\d{10,11}$/.test(nationalDigits);
    }
    if (countryCode === 'fr') {
        return /^\d{9}$/.test(nationalDigits);
    }
    return /^\d{8,15}$/.test(nationalDigits);
};
export const inferPhoneMetaFromValue = (rawValue, fallbackCountryCode = 'nl') => {
    const digits = digitsOnly(rawValue);
    const fallback = normalizeCountryCode(fallbackCountryCode) || 'nl';
    if (!digits) {
        return {
            countryCode: fallback,
            dialCode: inferDialCode(fallback, ''),
            phoneDigits: ''
        };
    }
    const countryCode = inferCountryCode('', '', digits) || inferCountryCode(fallback, '', digits) || fallback;
    const dialCode = inferDialCode(countryCode, '') || inferDialCode(fallback, '');
    return { countryCode, dialCode, phoneDigits: digits };
};
export const isPhoneValidForCountry = (rawValue, dialCode, countryCode) => {
    const digits = digitsOnly(rawValue);
    if (!digits)
        return true;
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    const explicitDialCode = digitsOnly(dialCode);
    const effectiveCountryCode = inferCountryCode(normalizedCountryCode, explicitDialCode, digits);
    const normalizedDialCode = inferDialCode(effectiveCountryCode, explicitDialCode);
    const nationalDigits = normalizeNationalDigits(effectiveCountryCode, stripDialCode(digits, normalizedDialCode));
    return isValidNationalDigits(effectiveCountryCode, nationalDigits);
};
